import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { http } from '@/shared/lib/api';
import { useAuth } from '@/shared/auth/AuthContext';

export interface TenantFeatures {
  companies: boolean;
  persons: boolean;
}

export interface TenantEntitlements {
  operators: number;
  flow_types: number;
  work_points: number;
  mobile_seats: number;
  documents_per_month: number;
  ai_monthly_token_budget: number;
  scale_count: number;
  flavor: 'simple' | 'deee_dba';
  modules: {
    treatment: boolean;
    pickup: boolean;
    nexus: boolean;
    accounting: boolean;
    market_prices: boolean;
    crm: boolean;
    signature_pad: boolean;
    id_scanner: boolean;
    analytics: boolean;
    smart_scales: boolean;
    partner_portal: boolean;
    ai_assistant: boolean;
    hr: boolean;
    siatd: boolean;
  };
}

export interface TenantProfileDTO {
  company_name: string | null;
  cui: string | null;
  viza_anuala_year: number | null;
  logo_storage_key: string | null;
  // White-label branding
  primary_color: string | null;
  secondary_color: string | null;
  // SMTP (smtp_pass never returned, only configured flag)
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_user: string | null;
  smtp_from_email: string | null;
  smtp_from_name: string | null;
  smtp_configured: boolean;
  // Financial
  financial_expense_alert_pct: number;
  // Operator-marked favorite collection-area locality — pre-fills the
  // locality field on new intake drafts. Distinct from the legal address.
  registry_number: string | null;
  share_capital: string | null;
  aviz_default_mention: string | null;
  bank_name: string | null;
  bank_account: string | null;
  favorite_locality: string | null;
  // Own working-day availability — same shape as company availability.
  availability: { days: number[]; exceptions: { date: string; available: boolean }[] } | null;
  // F9 — foreground-only GPS tracking for dispatch, opt-in per tenant.
  gps_tracking_enabled: boolean;
  // F9 backlog — fixed end-of-shift time ("HH:MM", 24h), null = no cutoff.
  gps_tracking_cutoff_time: string | null;
  // F14 HR — opt-in GPS at pontaj check-in, distinct legal basis from F9.
  hr_check_in_gps_enabled: boolean;
  pickup_request_email_enabled: boolean;
  // PLAN_CONTABILITATE.md Faza A follow-up — auto-create a DRAFT invoice
  // (never auto-issued) on intake/outgoing finalize for company sources.
  auto_create_invoice_on_finalize: boolean;
  // SAGA ART.xml <Tip> — required, tenant-configurable (SAGA's own "Tipuri
  // de articole/servicii" nomenclator).
  saga_article_type: string;
  features?: TenantFeatures;
  entitlements?: TenantEntitlements;
}

export interface UpdateTenantProfileBody {
  company_name?: string | null;
  cui?: string | null;
  viza_anuala_year?: number | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  smtp_host?: string | null;
  smtp_port?: number | null;
  smtp_user?: string | null;
  smtp_pass?: string | null;
  smtp_from_email?: string | null;
  smtp_from_name?: string | null;
  financial_expense_alert_pct?: number;
  registry_number?: string | null;
  share_capital?: string | null;
  aviz_default_mention?: string | null;
  bank_name?: string | null;
  bank_account?: string | null;
  favorite_locality?: string | null;
  availability?: { days: number[]; exceptions: { date: string; available: boolean }[] } | null;
  auto_create_invoice_on_finalize?: boolean;
  saga_article_type?: string;
  gps_tracking_enabled?: boolean;
  gps_tracking_cutoff_time?: string | null;
  hr_check_in_gps_enabled?: boolean;
  pickup_request_email_enabled?: boolean;
}

interface ItemEnvelope {
  success: true;
  data: TenantProfileDTO;
}

export const tenantProfileKey = ['tenant-profile'] as const;

export function useTenantProfile() {
  // GET /api/tenant/profile is operator-only — admin has no tenant, so
  // calling it for an admin user always 403s. AppLayout renders the same
  // nav shell for both variants and pulls in entitlements/features through
  // this hook regardless, so gate it here once rather than at every caller.
  const { user } = useAuth();
  return useQuery<ItemEnvelope>({
    queryKey: tenantProfileKey,
    queryFn: () => http.get<ItemEnvelope>('/api/tenant/profile'),
    staleTime: 30_000,
    enabled: user?.role !== 'admin',
  });
}

export function useUpdateTenantProfile() {
  const qc = useQueryClient();
  return useMutation<TenantProfileDTO, Error, UpdateTenantProfileBody>({
    mutationFn: async (body) => {
      const res = await http.patch<ItemEnvelope>('/api/tenant/profile', body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantProfileKey });
    },
  });
}
